import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type {
  NodeOverlay,
  PageGraphSource,
} from 'features/knowledgeDocumentViewer/graph/canvasTypes'
import {
  deduplicateAssociations,
  type GraphLiveAppearance,
  liveValueRectForOverlay,
  loadInstrumentAssociations,
  parseAssociatedDatapoint,
} from 'features/knowledgeDocumentViewer/graph/graphInstrumentation'
import { isPipeOffPageConnector } from 'features/knowledgeDocumentViewer/graph/offPageConnectorGraph'
import { useLiveTargetValues } from 'features/knowledgeDocumentViewer/hooks/useLiveTargetValues'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import { postFetcher } from 'features/rolodex/fetchers'
import type { MatchedDatapoint } from 'features/timeSeries'
import { useMemo } from 'react'
import useSWR from 'swr'
import { RolodexEntryType } from 'types'

import { buildNodeTooltip } from './nodeTooltip'
import { useIsGraphModeEnabled } from './useIsGraphModeEnabled'

type GraphInstrumentationOptions = {
  pageGraphs: PageGraphSource[]
  overlays: NodeOverlay[]
  documentId: string
  isActiveTab: boolean
}

type GraphInstrumentation = {
  appearances: GraphLiveAppearance[]
  datapointsByNodeId: Map<string, MatchedDatapoint[]>
  message: string | undefined
  retry: () => void
}

export function useGraphInstrumentation({
  pageGraphs,
  overlays,
  documentId,
  isActiveTab,
}: GraphInstrumentationOptions): GraphInstrumentation {
  const { t } = useLingui()
  const { orgID, siteID } = useKnowledgeDocumentDataContext()
  const { isLabelerMode, isEvalSessionLocked, activeTabId } =
    useKnowledgeDocumentUXContext()
  const graphMode = useIsGraphModeEnabled()
  const { selectedPipelineConfigId, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const scope = useMemo(
    () => ({
      organization: orgID,
      site: siteID,
      'pipeline-config': selectedPipelineConfigId ?? '',
    }),
    [orgID, siteID, selectedPipelineConfigId]
  )
  const enabled =
    graphMode &&
    isActiveTab &&
    !isLabelerMode &&
    !isEvalSessionLocked &&
    !isPipelineConfigScopeLoading &&
    isAuthenticated &&
    Boolean(selectedPipelineConfigId) &&
    pageGraphs.length > 0
  const graphs = useMemo(
    () => [...new Set(pageGraphs.map(({ graph }) => graph))],
    [pageGraphs]
  )
  const nodes = useMemo(
    () =>
      new Map(
        graphs.flatMap((graph) =>
          graph.getNodes().map((node) => [node.id, node] as const)
        )
      ),
    [graphs]
  )
  const sourceIds = useMemo(() => [...nodes.keys()], [nodes])
  const pageIds = useMemo(() => graphs.map((graph) => graph.pageId), [graphs])
  const localAssociations = useMemo(
    () =>
      deduplicateAssociations(
        graphs.flatMap((graph) =>
          graph.getDatapointEdges().map((edge) => ({
            sourceNodeId: edge.sourceNode.id,
            datapointId: edge.targetDatapointId,
          }))
        )
      ),
    [graphs]
  )
  const requestScope = useMemo(
    () => JSON.stringify([scope, activeTabId, documentId, pageIds, sourceIds]),
    [scope, activeTabId, documentId, pageIds, sourceIds]
  )
  const { data, error, mutate } = useSWR(
    enabled && sourceIds.length
      ? ['graph-instrument-associations', requestScope, ROLODEX_URL]
      : null,
    () =>
      loadInstrumentAssociations(sourceIds, scope, (query) =>
        postFetcher(
          `${ROLODEX_URL}/api/v2/entries/load`,
          JSON.stringify(query),
          authorizedAxios
        )
      ),
    { keepPreviousData: false }
  )
  const associations = useMemo(
    () =>
      enabled
        ? deduplicateAssociations([
            ...localAssociations,
            ...(data?.associations ?? []),
          ]).filter(({ sourceNodeId }) => nodes.has(sourceNodeId))
        : [],
    [enabled, localAssociations, data, nodes]
  )
  const datapointIds = useMemo(
    () => [...new Set(associations.map(({ datapointId }) => datapointId))],
    [associations]
  )
  const metadataOptions = useMemo(
    () => ({
      cacheScope: `graph-datapoints:${orgID}:${siteID}`,
      loadOptions: {
        type: [RolodexEntryType.DATA_POINTS],
        tags: { site: siteID },
      },
    }),
    [orgID, siteID]
  )
  const metadata = useRolodexEntriesCachedById(
    datapointIds,
    { keepPreviousData: false },
    metadataOptions
  )
  const targets = useMemo(() => {
    const requestedIds = new Set(datapointIds)
    const datapoints = new Map(
      (metadata.data ?? []).flatMap((entry) => {
        const parsed = parseAssociatedDatapoint(entry, scope)
        return parsed && requestedIds.has(parsed.id)
          ? [[parsed.id, parsed] as const]
          : []
      })
    )
    const bySource = new Map<string, string[]>()
    for (const { sourceNodeId, datapointId } of associations) {
      const ids = bySource.get(sourceNodeId) ?? []
      ids.push(datapointId)
      bySource.set(sourceNodeId, ids)
    }
    return [...bySource].flatMap(([nodeId, ids]) => {
      const node = nodes.get(nodeId)
      if (!node || isPipeOffPageConnector(node)) return []
      return [
        {
          key: nodeId,
          label: buildNodeTooltip(node).title,
          datapoints: ids.flatMap((id) => {
            const datapoint = datapoints.get(id)
            return datapoint ? [datapoint] : []
          }),
        },
      ]
    })
  }, [associations, datapointIds, metadata.data, scope, nodes])
  const readings = useLiveTargetValues(targets, requestScope, scope)
  const datapointsByNodeId = useMemo(
    () => new Map(targets.map(({ key, datapoints }) => [key, datapoints])),
    [targets]
  )
  const appearances = useMemo(() => {
    const linkedNodeIds = new Set(targets.map(({ key }) => key))
    const displayedReadings = new Set<string>()
    return overlays.flatMap((overlay) => {
      if (!linkedNodeIds.has(overlay.node.id)) return []
      const liveValue = readings.get(overlay.node.id)
      const appearanceKey = JSON.stringify([overlay.pageId, overlay.node.id])
      const valueRect =
        liveValue && !displayedReadings.has(appearanceKey)
          ? liveValueRectForOverlay(overlay)
          : undefined
      if (valueRect) displayedReadings.add(appearanceKey)
      return [
        {
          overlay,
          liveValue,
          valueRect,
        },
      ]
    })
  }, [overlays, targets, readings])
  return {
    appearances,
    datapointsByNodeId,
    message:
      enabled && (data?.failed || error || metadata.error)
        ? t`Some linked process data could not be loaded.`
        : undefined,
    retry: (): void => {
      void mutate()
      void metadata.mutate()
    },
  }
}
